/**
 * Roster filter predicates.
 *
 * One named predicate per sidebar section, all pure and all `true` when their
 * slice of the filter state is empty — so `filteredUsers` is a flat `&&` of
 * them and adding a filter never changes how the others behave. This is what
 * makes the brief's "filters that combine" goal hold: every dimension narrows
 * the same list independently.
 */
import { CareCoordinationStatus, RosterPatient } from '../../models/user';
import {
  ENROLLMENT_CANCELLED,
  ENROLLMENT_NEW,
  EnrollmentStatus,
  isPastTiming,
  NEW_SIGNUP_WINDOW_DAYS,
  RISK_FILTER_NA,
  VisitConfirmation,
  VisitTiming,
} from './constants';

export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether `user` signed up within the last `NEW_SIGNUP_WINDOW_DAYS` days.
 * Shared by the Enrollment Status "New" filter and the panel's "New This
 * Week" stat so the two definitions of "recent" can't drift apart. (It sat in
 * Care Coordination until round 6 moved it — see `matchesEnrollmentStatus`.)
 */
export const isRecentSignup = (user: RosterPatient, now: Date): boolean =>
  !!user.created_at &&
  new Date(user.created_at).getTime() >=
    now.getTime() - NEW_SIGNUP_WINDOW_DAYS * DAY_MS;

/**
 * Whether the member has left the program.
 *
 * Trivial enough to inline, and deliberately not inlined: the Enrollment
 * Status filter and the roster row's badge both ask this question, and if they
 * ever answered it differently the table would contradict the filter that
 * produced it. Same reasoning as `isRecentSignup` being shared with the "New
 * This Week" stat.
 */
export const isCancelled = (user: RosterPatient): boolean =>
  user.care_coordination_status === 'cancelled';

// --- next_meeting -----------------------------------------------------------

export type ParsedMeeting = {
  date: Date;
  /** Coach first name, when the visit is with a coach; '' otherwise. */
  coachFirstName: string;
  /** Full display name of whoever the visit is with. */
  attendee: string;
  confirmed: boolean;
};

/**
 * `next_meeting` arrives as `date|coachFirstName|attendee|confirmed`.
 *
 * Returns `null` for anything unparseable so callers can treat "no meeting"
 * and "malformed meeting" identically rather than rendering an Invalid Date.
 */
export const parseNextMeeting = (
  nextMeeting?: string | null
): ParsedMeeting | null => {
  if (!nextMeeting) return null;

  const [rawDate, coachFirstName = '', attendee = '', confirmed = ''] =
    nextMeeting.split('|');

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return null;

  return {
    date,
    coachFirstName,
    attendee,
    confirmed: confirmed === 't',
  };
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// --- Predicates -------------------------------------------------------------

export const matchesSearch = (user: RosterPatient, search: string): boolean => {
  if (!search) return true;

  const needle = search.toLowerCase();
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');

  return [name, user.email, user.id, user.healthie_id]
    .filter((v) => v !== undefined && v !== null && v !== '')
    .some((v) => String(v).toLowerCase().includes(needle));
};

/**
 * Care Team selection, grouped by role.
 *
 * **These groups AND against each other; names within a group OR.** Selecting
 * a provider and a coach asks for members who have *both* — per the team's
 * round-6 selection-logic framework ("show me all members who have both
 * 'Trent' and 'Stefania'").
 *
 * This deliberately reverses a rule that earlier rounds treated as
 * load-bearing: providers/NPs/RDs/pharmacists used to be flattened into one
 * OR pool precisely so that splitting the picklists by role could never turn
 * into ANDing the roles together. That AND was a bug twice, when it happened
 * *by accident* to picklists the team believed were one pool. It is now the
 * requested behavior for the one group that remains AND-able. If you are
 * reading this because AND-across-roles looks wrong, check the framework
 * before "fixing" it — the reversal is intentional.
 *
 * Only two groups remain as of round 8: Coach, and Providers. Providers now
 * covers every clinician role — MDs/DOs, NPs, Registered Dietitians, and
 * Pharmacists — searchable from one dropdown per team feedback, rather than
 * one dropdown per role. That reintroduces a flattened OR pool for
 * RD/pharmacist specifically, which reads like the exact bug the paragraph
 * above warns about — the difference is that this time it's the deliberate,
 * requested shape rather than an accident: there is no "Providers AND RDs"
 * reading to protect against because there is no separate RDs group anymore.
 * Providers and NPs were already one pool before this (see below); RDs and
 * pharmacists don't share a field with providers, but `matchesCareTeam`
 * checks a member's assignment across all three fields for a hit, so folding
 * their *picklists* together doesn't require folding their *data* together.
 *
 * Providers and NPs remain a single pool, and that is not negotiable by
 * product: they read the same patient field (`provider_first_name`/
 * `provider_last_name` — a member has one provider, who happens to be an MD,
 * DO, or NP), so ANDing them would return nobody by construction.
 *
 * The roster carries these people as name pairs rather than ids, so the join
 * is by name — see the note on `careTeamAttendees` below.
 */
export type CareTeamFilters = {
  /**
   * Coach ids, matched against `coach_id`. Its own AND group. `CareTeamSection`
   * caps this at one id (Aug 2026 filter-logic pass, single-select dropdown) —
   * the array shape stays so this predicate doesn't need to know that.
   */
  coachIds: string[];
  /**
   * Provider, NP, RD, and Pharmacist display names — one OR pool, see above.
   * Which patient field a given name actually matches against depends on the
   * role behind it (see `matchesCareTeam`); the caller doesn't need to know.
   * Same single-select cap as `coachIds` as of the Aug 2026 pass — OR-within-
   * the-pool is now moot in practice, but the predicate still just checks
   * array membership either way.
   */
  providerNames: string[];
};

const fullName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ');

const HONORIFIC = /^(dr|mr|mrs|ms|miss|prof)\.?\s+/i;

/**
 * Reduce a care-team name to something comparable across the two shapes the
 * app stores it in: picklists and `next_meeting` carry a full display name
 * with title and credentials ("Dr. Maya Patel, MD"), while the patient record
 * carries a bare first/last pair ("Maya", "Patel").
 *
 * Strips the credential suffix, then the honorific — dropping only one of the
 * two silently matches nothing, which is exactly how the provider filter
 * managed to look wired up while never returning a patient.
 */
const normalizeName = (raw: string) =>
  raw.split(',')[0].replace(HONORIFIC, '').trim().toLowerCase();

/**
 * Names of everyone currently selected across the Care Team section.
 *
 * Provider selections come in as care-team-member display names ("Dr. Maya
 * Patel, MD"), which is what `next_meeting` embeds. Coaches are stored on the
 * patient by id, so their attendee name is rebuilt from the patient record
 * instead.
 *
 * A flat union across every role group, unlike `matchesCareTeam` — and that
 * asymmetry is on purpose. This feeds the *ranking* signal below, which asks
 * "is this visit with anyone I selected", a question where one match is enough.
 * Membership is the question that ANDs.
 */
export const careTeamAttendees = (
  user: RosterPatient,
  filters: CareTeamFilters
): string[] => {
  const names = [...filters.providerNames];

  if (filters.coachIds.includes(user.coach_id)) {
    names.push(fullName(user.coach_first_name, user.coach_last_name));
  }

  return names;
};

/**
 * Is this member's next visit with one of the care-team members selected in
 * the sidebar — i.e. "with me"?
 *
 * This used to be a *filter* (`matchesVisitStatus`'s old `scopedAttendees`).
 * It is now purely a ranking signal: visits with the selected care team sort
 * above visits with anyone else rather than hiding the latter. False when
 * nothing is selected, which collapses the two tiers into one and leaves the
 * plain soonest-first order.
 */
export const isVisitWithCareTeam = (
  user: RosterPatient,
  filters: CareTeamFilters
): boolean => {
  const meeting = parseNextMeeting(user.next_meeting);
  if (!meeting) return false;

  return careTeamAttendees(user, filters).some(
    (name) => normalizeName(name) === normalizeName(meeting.attendee)
  );
};

/**
 * Every clinician name assigned to a member, across the three fields the
 * Providers dropdown now covers (provider, RD, pharmacist — NP shares the
 * provider field, so it needs no field of its own).
 *
 * A member only ever has a name in whichever field their actual role fills —
 * a provider name never shows up as the RD assignment — so there's no risk
 * of a selection matching the wrong field for the wrong reason; this just
 * saves `matchesRole` from needing to know which field a given selected name
 * came from.
 */
const assignedCareTeamMemberNames = (user: RosterPatient): string[] =>
  [
    fullName(user.provider_first_name, user.provider_last_name),
    fullName(user.rd_first_name, user.rd_last_name),
    fullName(user.pharmacist_first_name, user.pharmacist_last_name),
  ].filter(Boolean);

/**
 * Does the member's Providers-dropdown assignment satisfy the selection?
 *
 * `true` when nothing is selected, so an untouched dropdown never narrows
 * anything — the same "empty means no filter" contract every predicate in this
 * file keeps. A member with *no* assignment in any of the three fields fails
 * as soon as the dropdown has a selection: asking for "Dr. Patel's patients"
 * shouldn't return members who have no provider at all.
 *
 * Matched on name because that is all the roster carries for these roles — the
 * real care-team-member endpoint returns no role, so there are no per-role ids
 * to join on. Stopgap; revisit when the API distinguishes roles. See
 * CareTeamMemberOption.type in fixtures/roster.
 */
const matchesRole = (assigned: string[], selected: string[]): boolean => {
  if (!selected.length) return true;
  if (!assigned.length) return false;

  return assigned.some((name) =>
    selected.some((selectedName) => normalizeName(selectedName) === normalizeName(name))
  );
};

/**
 * Is this member on the selected care team?
 *
 * One AND group per role (Coach, Providers), OR within each — see
 * `CareTeamFilters` for why, and for why Providers now covers every
 * clinician role rather than getting one dropdown each.
 */
export const matchesCareTeam = (
  user: RosterPatient,
  { coachIds, providerNames }: CareTeamFilters
): boolean => {
  if (coachIds.length && !coachIds.includes(user.coach_id)) return false;

  if (!matchesRole(assignedCareTeamMemberNames(user), providerNames)) {
    return false;
  }

  return true;
};

export type MemberStatusFilters = {
  careModels: string[];
  /** Risk levels, plus the filter-only `n/a` for members with no rating. */
  risks: string[];
};

export const matchesMemberStatus = (
  user: RosterPatient,
  { careModels, risks }: MemberStatusFilters
): boolean => {
  if (careModels.length && !careModels.includes(user.care_model)) return false;

  if (risks.length) {
    const risk = user.risk_stratification;
    const value = risk ?? RISK_FILTER_NA;
    if (!risks.includes(value)) return false;
  }

  return true;
};

export type VisitStatusFilters = {
  timing: VisitTiming | null;
  confirmations: VisitConfirmation[];
};

/**
 * Does this member's next visit fall in the requested window?
 *
 * **Attendee-blind on purpose (Aug 2026 round 4).** These predicates used to
 * only count a visit if it was with the care-team member(s) selected in the
 * sidebar — round 1's fix for the brief's complaint #3, that filtering by
 * coach surfaced anyone on that coach's panel with a visit with *anybody*.
 *
 * That was reconsidered: hiding the visit was the wrong half of the fix. A
 * provider looking at their panel needs to see that a member has care coming
 * up this week even when it's a coach or an RD they're seeing — that is often
 * exactly the thing worth knowing. So who a visit is *with* no longer decides
 * whether the member appears; it decides where they rank. See
 * `isVisitWithCareTeam` and the two-tier sort in `useHealthCoachDashboard`.
 *
 * Which members appear at all is still scoped by the Care Team filters via
 * `matchesCareTeam` — that's panel membership, a separate question from whose
 * appointment it is, and it is unchanged.
 *
 * The custom date-range inputs this used to support are gone (round 7),
 * replaced by the `last_week`/`last_month` presets. The range existed for
 * "none of the buckets fit", and looking backwards was the case it was
 * actually being used for — two named buckets say it in one click and can't
 * be filled in contradictorily.
 */
export const matchesVisitStatus = (
  user: RosterPatient,
  { timing, confirmations }: VisitStatusFilters,
  now: Date
): boolean => {
  if (!timing && !confirmations.length) return true;

  const meeting = parseNextMeeting(user.next_meeting);

  const upcoming = meeting !== null && meeting.date >= now;
  // "Today" covers the whole day, not just the rest of it — someone
  // reviewing today's visits at 4pm still means the 9am one.
  const isToday = meeting !== null && isSameDay(meeting.date, now);
  // Strictly before today. `isToday` is excluded so a visit earlier today
  // counts as scheduled rather than past, matching how every other bucket
  // here treats today as a whole day.
  const isPast = meeting !== null && !upcoming && !isToday;

  if (timing === 'none_scheduled') {
    // "Nothing scheduled" means nothing upcoming *with anyone*: a member
    // seeing their coach next week is not unbooked just because the visit
    // isn't with the provider running the filter (team decision, round 4).
    //
    // `isToday` is in here alongside `upcoming` on purpose. A visit earlier
    // today is not "upcoming", but the row still renders its date and time,
    // so counting that member as unbooked put a visible appointment in a
    // list captioned "None Scheduled". Today's visits count as scheduled
    // for the whole day, matching how the Today bucket already treats them.
    // A visit that happened last week still leaves a member unbooked — which
    // is now a reachable case rather than a hypothetical one (round 7 gave
    // the fixtures past appointments), and is deliberately still true: a
    // member whose only appointment has already happened has nothing booked,
    // and the roster shows them "None scheduled" to match.
    if (upcoming || isToday) return false;
  } else if (timing === 'today') {
    if (!isToday) return false;
  } else if (timing === 'last_week' || timing === 'last_month') {
    // Backward-looking, and the mirror image of this_week/this_month: rolling
    // windows measured back from now rather than forward. A member matches on
    // an appointment that already happened, which is the only place in the
    // roster where a past appointment is surfaced at all — see
    // `isPastTiming`'s doc comment and the row's display gate.
    if (!isPast || !meeting) return false;

    const daysBack = timing === 'last_week' ? 7 : 30;
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - daysBack);
    if (meeting.date < cutoff) return false;
  } else if (timing) {
    if (!upcoming || !meeting) return false;

    // Rolling windows, not calendar week/month boundaries — "this week" on
    // a Friday means the next 7 days, not through Sunday. "This month"
    // mirrors that same rolling design at 30 days instead of a fixed
    // "through Aug 31st".
    if (timing === 'this_week') {
      const weekOut = new Date(now);
      weekOut.setDate(weekOut.getDate() + 7);
      if (meeting.date > weekOut) return false;
    } else if (timing === 'this_month') {
      const monthOut = new Date(now);
      monthOut.setDate(monthOut.getDate() + 30);
      if (meeting.date > monthOut) return false;
    }
  }

  if (confirmations.length) {
    // Also attendee-blind: an unconfirmed visit with a coach is still an
    // unconfirmed visit worth chasing, and having these pills mean something
    // different from the timing controls sitting beside them would be a trap.
    //
    // **Timing-blind too, and that is a decision rather than an oversight**
    // (team call, round 7). These match on the appointment's confirmed flag
    // whether it is upcoming or already past, so a past-window result narrows
    // by confirmation like any other, and `Confirmed` on its own returns
    // members whose appointment has already happened — currently 26 of them,
    // whose rows read "None scheduled" in the default view.
    //
    // The argument for excluding past appointments is that "will this happen"
    // is a meaningless question about something that already did. The team
    // weighed that and chose to leave the pills applying to both, so don't
    // "fix" this into `upcoming`-only without going back to them: it would
    // silently shrink every past-window result that has a confirmation
    // selected. Disabling the control in past-window mode was the alternative
    // considered and explicitly deferred — it needs a per-option `disabled` on
    // the shared `PillToggleGroup`, which has none today.
    if (!meeting) return false;
    const state: VisitConfirmation = meeting.confirmed
      ? 'confirmed'
      : 'unconfirmed';
    if (!confirmations.includes(state)) return false;
  }

  return true;
};

/**
 * Care Coordination match — one funnel stage, or none.
 *
 * Single-select as of round 6, which is what let this collapse to an equality
 * check. It used to take an array that mixed real funnel values with a `'new'`
 * sentinel, pulling the sentinel out to AND it while the funnel values OR'd
 * against each other. Both halves of that are gone: "New" moved to Enrollment
 * Status (see `matchesEnrollmentStatus`), and with one stage selectable at a
 * time there is no longer an OR to express.
 *
 * The old shape wasn't arbitrary — a member occupies exactly one funnel stage,
 * so multi-select could only ever mean OR, never AND. Single-select says the
 * same thing with less room to get it wrong.
 */
export const matchesCareCoordination = (
  user: RosterPatient,
  status: CareCoordinationStatus | null
): boolean => {
  if (!status) return true;

  return user.care_coordination_status === status;
};

/**
 * Enrollment Status match — where the member stands relative to the program,
 * rather than where they are in the care funnel.
 *
 * Both values reach across to other signals rather than reading a field of
 * their own: `new` is signup recency via the shared `isRecentSignup` (same
 * definition the "New This Week" stat uses, so the two can't drift), and
 * `cancelled` reads `care_coordination_status`.
 *
 * That second one means selecting Cancelled here alongside any Care
 * Coordination stage returns nobody, since both read the same field and a
 * member can only hold one value of it. That's honest rather than broken — a
 * cancelled member genuinely doesn't occupy a live funnel stage — so it's left
 * to produce an empty result instead of being papered over with cross-group
 * disabling logic.
 */
export const matchesEnrollmentStatus = (
  user: RosterPatient,
  status: EnrollmentStatus | null,
  now: Date
): boolean => {
  if (!status) return true;

  if (status === ENROLLMENT_NEW) return isRecentSignup(user, now);
  if (status === ENROLLMENT_CANCELLED) return isCancelled(user);

  return true;
};

/**
 * The appointment a row should display, or `null` for "None scheduled".
 *
 * **Past appointments are hidden unless explicitly asked for** (team decision,
 * round 7). In the default view and the forward-looking buckets the column
 * only ever shows an upcoming appointment, so its "Next Appointment" header is
 * always accurate and a stale date can't masquerade as a booking. Select a
 * past window and the same cell shows the past appointment, because that is
 * precisely what was asked for.
 *
 * Note this hides the *appointment*, never the *member*: someone whose only
 * appointment has already happened stays on the panel showing "None
 * scheduled", and is still returned by the None Scheduled filter. Dropping
 * them from the roster would make "My Total Panel" stop meaning the panel and
 * would strand them behind a filter nobody would think to reach for.
 *
 * Centralized here rather than in the row because three things have to agree
 * about it — what renders, how the list sorts, and what the column header says.
 */
export const displayedAppointment = (
  user: RosterPatient,
  timing: VisitTiming | null,
  now: Date
): ParsedMeeting | null => {
  const meeting = parseNextMeeting(user.next_meeting);
  if (!meeting) return null;

  if (isPastTiming(timing)) return meeting;

  // Today counts as upcoming for the whole day, matching every timing bucket.
  return meeting.date >= now || isSameDay(meeting.date, now) ? meeting : null;
};

export type AttributeFilters = {
  userTypes: string[];
  conditions: string[];
};

export const matchesAttributes = (
  user: RosterPatient,
  { userTypes, conditions }: AttributeFilters
): boolean => {
  if (userTypes.length && !userTypes.includes(user.user_type)) return false;

  // A member can carry several conditions; selecting any one of them should
  // surface them, so this is an overlap test rather than a subset test.
  if (conditions.length) {
    const owned = user.condition_categories ?? [];
    if (!owned.some((c) => conditions.includes(c))) return false;
  }

  return true;
};
